import { FC } from "react";
import { createPortal } from "react-dom";
import { getTrialLessonTagName, ITrialLesson } from "../../../../entities/trialLesson";
import { formatLessonsCount } from "../../../../shared/lib/helpers/formatRuCount";
import { useCalendarEventsPopover } from "../../../../shared/lib/hooks/useCalendarEventsPopover";
import { StudentIcon, TutorIcon } from "../../../../shared/ui/icon";
import classes from '../trialCalendarDay.module.scss'

interface IProps {
    trialLessons: ITrialLesson[];
    top: number;
    left: number;
    width: number;
    height: number;
    zIndex: number;
    openToLeft: boolean;
    getTrialLessonTimeRange: (trialLesson: ITrialLesson) => string;
    getTrialLessonStatusClass: (status: ITrialLesson['status']) => string;
    statusClassName: string;
    onSelectTrialLesson: (trialLesson: ITrialLesson) => void;
}

export const TrialLessonsPopover: FC<IProps> = ({
    trialLessons, top, left, width, height, zIndex, openToLeft,
    getTrialLessonTimeRange, getTrialLessonStatusClass, statusClassName,
    onSelectTrialLesson,
}) => {
    const {
        isOpen,
        setIsOpen,
        containerRef,
        popoverRef,
        isMobile,
    } = useCalendarEventsPopover()
    const lessonsCountLabel = formatLessonsCount(trialLessons.length)

    const lessonsPopover = (
        <section
            ref={popoverRef}
            className={
                `${classes.lessonsPopover} ` +
                (isMobile
                    ? classes.mobileLessonsPopover
                    : openToLeft
                        ? classes.popoverToLeft
                        : classes.popoverToRight
                )
            }
            onPointerDown={event => event.stopPropagation()}
        >
            <section className={classes.popoverHeader}>
                <span className={classes.popoverTitle}>Пробные в это время</span>
                <button
                    type="button"
                    className={classes.closePopoverButton}
                    onClick={() => setIsOpen(false)}
                    aria-label="Закрыть список пробных занятий"
                >
                    ×
                </button>
            </section>
            <section className={classes.popoverList}>
                {trialLessons.map(trialLesson => {
                    const lessonTagName = getTrialLessonTagName(trialLesson)

                    return (
                        <button
                            type="button"
                            key={trialLesson.id}
                            className={
                                `${classes.popoverLesson} ` +
                                getTrialLessonStatusClass(trialLesson.status)
                            }
                            onClick={() => onSelectTrialLesson(trialLesson)}
                        >
                            <section className={classes.popoverLessonHeader}>
                                <span className={classes.popoverLessonTime}>
                                    {getTrialLessonTimeRange(trialLesson)}
                                </span>
                                <span className={classes.popoverLessonTag}>{lessonTagName}</span>
                            </section>
                            <strong className={classes.popoverStudent}>
                                <StudentIcon size={15} />
                                <span>{trialLesson.studentName}</span>
                            </strong>
                            <span className={classes.popoverDetails}>
                                <TutorIcon size={14} />
                                <span>{trialLesson.tutor.fullName}</span>
                                <span className={classes.popoverSubject}>{lessonTagName}</span>
                            </span>
                        </button>
                    )
                })}
            </section>
        </section>
    )

    return (
        <section
            ref={containerRef}
            className={classes.moreLessons}
            style={{
                top,
                left,
                width,
                maxWidth: `calc(100% - ${left}px)`,
                zIndex: isOpen ? 10000 - top : zIndex,
            }}
            onPointerDown={event => event.stopPropagation()}
        >
            <button
                type="button"
                className={`${classes.moreLessonsButton} ${statusClassName}`}
                style={{height}}
                onClick={() => setIsOpen(value => !value)}
                title={`Открыть: ${lessonsCountLabel}`}
            >
                <strong className={classes.moreLessonsCount}>{lessonsCountLabel}</strong>
            </button>
            {isOpen && (
                isMobile
                    ? createPortal(
                        <section
                            className={`${classes.moreLessons} ${classes.mobilePopoverOverlay}`}
                            onPointerDown={() => setIsOpen(false)}
                        >
                            {lessonsPopover}
                        </section>,
                        document.body
                    )
                    : lessonsPopover
            )}
        </section>
    )
}
